"use client"

import * as React from "react"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
  FloatingSheetPanel,
  FloatingSheetPanelBody,
  FloatingSheetPanelContent,
  FloatingSheetPanelFooter,
  FloatingSheetPanelHeader,
  FloatingSheetPanelToolbar,
  railTriggerProps,
} from "@/lib/floating-sheet-panel"
import { DashboardSectionTitle } from "@/components/dashboard-section-heading"
import {
  QUALITY_ATTENTION_ITEMS,
  type QualityAttentionItem,
  type QualityPriority,
} from "@/lib/mock/evaluation-quality-dashboard"

function PriorityBadge({ priority }: { priority: QualityPriority }) {
  if (priority === "high") {
    return (
      <Badge variant="destructive" className="capitalize">
        {priority}
      </Badge>
    )
  }
  if (priority === "medium") {
    return (
      <Badge variant="outline" className="border-chart-4/40 capitalize text-[color:var(--chart-4)]">
        {priority}
      </Badge>
    )
  }
  return (
    <Badge variant="secondary" className="capitalize">
      {priority}
    </Badge>
  )
}

export function QualityNeedsAttention() {
  const [selected, setSelected] = React.useState<QualityAttentionItem | null>(null)
  const [shown, setShown] = React.useState<QualityAttentionItem | null>(null)

  if (selected && selected !== shown) setShown(selected)

  const shownIndex = shown
    ? QUALITY_ATTENTION_ITEMS.findIndex(item => item.id === shown.id)
    : -1

  function openItem(item: QualityAttentionItem) {
    setSelected(item)
  }

  return (
    <>
      <section aria-labelledby="quality-attention-heading" className="flex h-full flex-col gap-4">
        <div className="flex items-end justify-between gap-3">
          <div className="flex flex-col gap-1">
            <DashboardSectionTitle as="h2" id="quality-attention-heading">
              Needs attention
            </DashboardSectionTitle>
            <p className="text-sm text-muted-foreground">
              {QUALITY_ATTENTION_ITEMS.length} areas require review
            </p>
          </div>
          <Button variant="ghost" size="sm" className="shrink-0">
            View all {QUALITY_ATTENTION_ITEMS.length}
          </Button>
        </div>

        <div className="overflow-x-auto rounded-xl border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Area</TableHead>
                <TableHead>Issue</TableHead>
                <TableHead>Signal</TableHead>
                <TableHead className="w-[1%]">Priority</TableHead>
                <TableHead className="w-[1%]">
                  <span className="sr-only">Action</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {QUALITY_ATTENTION_ITEMS.map(item => (
                <TableRow
                  key={item.id}
                  {...railTriggerProps()}
                  className="cursor-pointer"
                  data-state={selected?.id === item.id ? "selected" : undefined}
                  onClick={() => openItem(item)}
                >
                  <TableCell className="font-medium">{item.area}</TableCell>
                  <TableCell className="text-muted-foreground">{item.issue}</TableCell>
                  <TableCell className="tabular-nums">{item.signal}</TableCell>
                  <TableCell>
                    <PriorityBadge priority={item.priority} />
                  </TableCell>
                  <TableCell>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      {...railTriggerProps()}
                      onClick={event => {
                        event.stopPropagation()
                        openItem(item)
                      }}
                    >
                      Review
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </section>

      <FloatingSheetPanel open={selected != null} onOpenChange={open => !open && setSelected(null)}>
        <FloatingSheetPanelContent size="md" contentSlot="quality-attention-detail">
          <FloatingSheetPanelToolbar
            onPrevious={
              shownIndex > 0
                ? () => setSelected(QUALITY_ATTENTION_ITEMS[shownIndex - 1]!)
                : undefined
            }
            onNext={
              shownIndex >= 0 && shownIndex < QUALITY_ATTENTION_ITEMS.length - 1
                ? () => setSelected(QUALITY_ATTENTION_ITEMS[shownIndex + 1]!)
                : undefined
            }
            previousLabel="Previous area"
            nextLabel="Next area"
          />
          <FloatingSheetPanelHeader
            title={shown?.area ?? "Needs attention"}
            description={shown?.whyFlagged.headline}
            subtitle={
              shownIndex >= 0
                ? `${shownIndex + 1} of ${QUALITY_ATTENTION_ITEMS.length}`
                : undefined
            }
          />
          <FloatingSheetPanelBody>
            {shown ? (
              <div className="flex flex-col gap-4 px-4 pb-6">
                <p className="text-sm font-medium text-foreground">Why was this flagged?</p>
                <ul className="flex list-disc flex-col gap-2 ps-4 text-sm text-muted-foreground">
                  {shown.whyFlagged.bullets.map(bullet => (
                    <li key={bullet}>{bullet}</li>
                  ))}
                </ul>
              </div>
            ) : null}
          </FloatingSheetPanelBody>
          {shown ? (
            <FloatingSheetPanelFooter>
              <Button size="sm">Create action</Button>
            </FloatingSheetPanelFooter>
          ) : null}
        </FloatingSheetPanelContent>
      </FloatingSheetPanel>
    </>
  )
}
